import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { ADMIN_MANY_ORDERS_API_PATHS } from '../constants/orders-api-paths.constant';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { Order } from '../types/order.type';

interface OrderStats {
  total: number;
  totalPaid: number;
  count: number;
  productsCount: number;
}

interface OrdersToArchiveResponse {
  orders: Order[];
  stats: OrderStats;
}

interface OrdersToArchiveState {
  loading: boolean;
  orders: Order[];
  stats: OrderStats | null;
}

export interface UseGetOrdersToArchiveParams {
  deliveryDate: string;
  ownerRole?: string;
}

export function useGetOrdersToArchive({ deliveryDate, ownerRole }: UseGetOrdersToArchiveParams) {
  const { enqueueSnackbar } = useSnackbar();

  const [result, setResult] = useState<OrdersToArchiveState>({
    loading: true,
    orders: [],
    stats: null,
  });

  const fetchOrders = useCallback(() => {
    if (!deliveryDate) return;

    setResult((prev) => ({ ...prev, loading: true }));

    const url = `${ADMIN_MANY_ORDERS_API_PATHS.TO_ARCHIVE}/${deliveryDate}`;

    HttpClient.get<OrdersToArchiveResponse>(url, {
      params: { ownerRole: ownerRole !== 'all' ? ownerRole : undefined },
    })
      .then((response) => {
        setResult({
          loading: false,
          orders: response.orders,
          stats: response.stats,
        });
      })
      .catch((error) => {
        const message =
          error.response?.data?.message ?? `Une erreur est survenue lors de la récupération des commandes`;
        enqueueSnackbar(message, { variant: 'error' });
        setResult({ loading: false, orders: [], stats: null });
      });
  }, [deliveryDate, ownerRole]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return { ...result, fetchOrders };
}
